import { defineLocalStore } from '@/utils/localStore'
import { useRoute, useRouter } from 'vue-router'
import { computed, reactive, ref } from 'vue'
import { DocumentSignature } from '@/utils/types/documentSignature'
import axios from '@/plugins/axios'
import { handleAsync, confirm } from 'best-modules/plugins'
import {
  urlDocumentSendToAgree,
  urlDocumentSignatureArchive,
  urlDocumentSignatureDelete,
  urlDocumentSignatureProtocol,
  urlDocumentSignatureRepack,
  urlDocumentSignatureSingle,
} from '@/utils/urls'
import { useBreadcrumb, useStore } from '@/plugins'
import { documentSignatureSingleChannel, socket } from '@/socket'
import { keepArray } from 'best-modules/utils'
import { download } from 'best-modules/vue-utils'
import { openDialog } from '@/dialog'
import { cloneDeep } from 'lodash'
import { useCan } from '@/composables/useCan'

export const {
  useLocalStore: useDocumentSignature,
  initLocalStore: initDocumentSignature,
} = defineLocalStore('document-signature', () => {
  const store = useStore()
  const route = useRoute()
  const router = useRouter()
  const breadcrumb = useBreadcrumb()
  const { can } = useCan()
  const documentSignatureId = computed<number>(() => {
    return Number(route.params.id)
  })

  const documentSignature = reactive<DocumentSignature>({} as DocumentSignature)
  const userId = computed<number>(() => store.state.user.userData?.id)
  const documentPath = ref<string>('')
  const isAuthor = computed<boolean>(
    () => documentSignature.authorId === userId.value
  )
  const isErm = computed<boolean>(() => !!documentSignature.ermId)
  const watermarks = computed(() => {
    return keepArray(documentSignature.participants).map(p => {
      return {
        isStamp: !!p.certificate.extKeyUsages,
        text: [
          p.certificate.subjOrg,
          p.certificate.extKeyUsages ? null : p.certificate.subjCN,
          'ЄДРПОУ/ІПН',
          p.certificate.subjEDRPOUCode || p.certificate.subjDRFOCode || '---',
        ]
          .filter(Boolean)
          .join(' '),
      }
    })
  })

  const getDocumentSignature = () => {
    return handleAsync('getDocumentSignature', async () => {
      const { data } = await axios.get(
        urlDocumentSignatureSingle(documentSignatureId.value)
      )

      Object.assign(documentSignature, data)
      breadcrumb.set([
        {
          title: 'Підписання',
          to: {
            name: 'document-signature-list',
          },
          index: 0,
        },
        { title: documentSignature.name, index: 1 },
      ])
    })
  }

  getDocumentSignature().then(() => {
    socket.on(documentSignatureSingleChannel, res => {
      if (res.id === documentSignatureId.value) {
        Object.assign(documentSignature, res)
      }
    })
  })

  const downloadProtocol = () => {
    return axios
      .get(urlDocumentSignatureProtocol(documentSignatureId.value))
      .then(res => {
        download(res.data.path, res.data.path.split('/').at(-1))
      })
  }

  const downloadArchive = () => {
    can('document-signatures.archive', () => {
      return axios
        .get(urlDocumentSignatureArchive(documentSignatureId.value))
        .then(res => {
          download(res.data.path, res.data.path.split('/').at(-1))
        })
    })
  }
  const downloadP7S = () => {
    return axios
      .get(urlDocumentSignatureRepack(documentSignatureId.value))
      .then(res => {
        download(res.data.path, res.data.path.split('/').at(-1))
      })
  }

  const downloadDocument = () => {
    return download(documentPath.value, documentSignature.document[0].name)
  }

  const deleteDocument = () => {
    can('document-signatures.delete', () => {
      confirm({
        text: 'Ви впевненні що хочете видалити документ на завжди?',
      }).then(() => {
        return handleAsync('deleteDocument', () => {
          return axios
            .delete(urlDocumentSignatureDelete(documentSignatureId.value))
            .then(() => {
              router.push({
                name: 'document-signature-notes',
              })
            })
        })
      })
    })
  }

  const sendToAgree = () => {
    can('document-signatures.convertToAgreement', () => {
      return handleAsync('sendToAgree', () => {
        return axios
          .patch(urlDocumentSendToAgree(documentSignatureId.value))
          .then(res => {
            router.push({
              name: 'document-agreement-single',
              params: { id: res.data.id },
            })
          })
      })
    })
  }

  const openEditDocumentDialog = () => {
    can('document-signatures.update', () => {
      openDialog({
        name: 'DocumentSignature',
        action: 'update',
        dialogData: {
          document: cloneDeep(documentSignature),
          disabled: !isAuthor.value || documentSignature.statusId !== 1,
        },
        params: {
          cb: async updatedDocument => {
            Object.assign(documentSignature, updatedDocument)
          },
          submitLabel: 'Зберегти',
        },
      })
    })
  }

  return {
    documentSignatureId,
    documentSignature,
    documentPath,
    userId,
    isAuthor,
    isErm,
    watermarks,
    deleteDocument,
    sendToAgree,
    downloadProtocol,
    downloadArchive,
    downloadP7S,
    downloadDocument,
    openEditDocumentDialog,
  }
})
